import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import InteractorCorrectionApproval from '../../../interactors/transaction/correction/InteractorCorrectionApproval'
import { confirmationAlert } from '../../../utility/SweetAlert'
import { t } from 'i18next'
import InteractorShippingHeaderSearch from '../../../interactors/transaction/shipping/InteractorShippingHeaderSearch'
import { InteractorOrderSearch } from '../../../interactors/Main'
import { useSelector } from 'react-redux'

const headers = [
  { label: t('fee.created_date'), key: 'created_date' },
  { label: t('form.correction_number'), key: 'correction_code' },
  { label: t('form.reference_number_internal'), key: 'reference_number_internal' },
  { label: t('form.correction_note'), key: 'correction_note' },
  { label: t('title.status'), key: 'initial_status' },
  { label: t('title.action'), key: 'actions' },
]

const renderStatus = (status) => {
  const base = 'badge rounded-pill px-3 py-1 fw-medium'
  switch (status) {
    case 'REQUEST':
      return (
        <span className={`${base} bg-warning text-dark`}>{t('status.bill.WAITING_APPROVAL')}</span>
      )
    case 'APPROVED':
      return <span className={`${base} bg-success text-white`}>{t('status.bill.APPROVED')}</span>
    case 'CANCELLED':
      return <span className={`${base} bg-danger text-white`}>{t('status.bill.CANCELLED')}</span>
    case 'REJECTED':
      return <span className={`${base} bg-black text-white`}>{t('status.bill.REJECTED')}</span>
    default:
      return <span className={`${base} bg-secondary`}>{status}</span>
  }
}

const OrderCorrectionTable = (dataItems) => {
  const { t } = useTranslation()
  const { account } = useSelector((state) => state.account)
  const pathSegments = window.location.pathname.split('/')
  const uid = pathSegments[pathSegments.length - 1]
  const { correctionApproval } = InteractorCorrectionApproval()
  const { shippingHeaderSearch } = InteractorShippingHeaderSearch()
  const { orderSearch } = InteractorOrderSearch()
  const [loading, setLoading] = useState(true)
  const [loadingShipping, setLoadingShipping] = useState(true)
  const [orderData, setOrderData] = useState([])
  const [dataShipping, setDataShipping] = useState([])
  const [payload] = useState({
    page: 1,
    limit: 5,
    keywords: {
      text_search: '',
      status: '',
      payment_type: '',
      account_uid: account.uid,
      store_uid: '',
      uid,
    },
  })
  const [payloadShipping] = useState({
    page: 1,
    limit: 50,
    keywords: {
      text_search: '',
      uid: '',
      transaction_uid: uid,
      account_uid: account?.uid,
      status: '',
      store_uid: '',
    },
  })
  const [finalResult, setFinalResult] = useState(null) // null | 'ada' | 'gada'

  const isOrderReady = Array.isArray(orderData?.items) // sudah kebentuk array (meski kosong)
  const isShippingReady = dataShipping?.items !== undefined // sudah kebaca dari API (bisa null/array)
  const hasItems = (orderData?.items?.length ?? 0) > 0
  const billRaw = orderData?.items?.[0]?.bill ?? '[]'
  const billList = orderData?.items?.[0]?.bill
  const { data } = dataItems
  const { datacard } = data
  const { items } = datacard

  const checkOrderStatus = useMemo(() => {
    return ['COMPLETED', 'CANCELLED', 'CLOSE_ORDER_REQUEST'].includes(orderData?.items?.[0]?.status)
  }, [orderData?.items])

  const checkBillStatus = useMemo(() => {
    try {
      return JSON.parse(billRaw).some((b) => ['ISSUED', 'PROCESSED'].includes(b.status))
    } catch {
      return false
    }
  }, [billRaw])

  const hasShippedStatus = useMemo(() => {
    if (!Array.isArray(dataShipping?.items) || dataShipping.items.length === 0) return false
    return dataShipping.items.some(
      (it) => !['DELIVERED', 'CANCELLED', 'CORRECTION'].includes(it.initial_status),
    )
  }, [dataShipping?.items])

  const handleResponse = useCallback((data) => {
    setOrderData(data)
  }, [])

  const handleResponseShipping = useCallback((data) => setDataShipping(data), [])

  useEffect(() => {
    if (loading) {
      orderSearch(payload, handleResponse, setLoading)
    }
  }, [handleResponse, loading, payload, orderSearch])

  useEffect(() => {
    if (loadingShipping) {
      shippingHeaderSearch(payloadShipping, handleResponseShipping, setLoadingShipping)
    }
  }, [shippingHeaderSearch, handleResponseShipping, loadingShipping, payloadShipping])

  useEffect(() => {
    if (!isOrderReady || !isShippingReady) return

    if (finalResult !== null) return

    const shouldStay =
      (hasItems && checkOrderStatus) ||
      (hasItems && !hasShippedStatus && !checkBillStatus && !checkOrderStatus) ||
      (billList === undefined && dataShipping?.items === null)

    setFinalResult(shouldStay)
  }, [
    isOrderReady,
    isShippingReady,
    hasItems,
    hasShippedStatus,
    checkBillStatus,
    billList,
    dataShipping?.items,
    finalResult,
    checkOrderStatus,
  ])

  useEffect(() => {
    if (finalResult === null) return

    if (!finalResult) window.location.href = `/order/view/${uid}`
  }, [finalResult, uid])

  const cancelCorrection = async (e, data) => {
    e.preventDefault()

    let payload = {
      uid: data.uid,
      status: 'CANCELLED',
      correction_note: data.correction_note,
    }
    const confirm = await confirmationAlert({
      title: t('title.are_you_sure'),
      message: t('message.confirmation_cancel_correction'),
      icon: 'warning',
    })
    if (confirm) correctionApproval(payload, true)
  }

  return (
    <div className="w-100 px-3 py-5">
      <div className="d-flex justify-content-end  align-items-center mb-4 flex-wrap gap-2">
        {(items === null || items[0]?.initial_status !== 'REQUEST') &&
          !['COMPLETED', 'CANCELLED', 'CLOSE_ORDER_REQUEST'].includes(
            orderData?.items?.[0]?.status,
          ) &&
          ((orderData?.items?.length > 0 && !hasShippedStatus && !checkBillStatus) ||
            (orderData?.items?.[0]?.bill === undefined && dataShipping?.items === null)) && (
            <Link
              to={`/order/correction/create/${uid}`}
              className="d-inline btn rounded-pill btn-primary btn-xs m-2 font-size-10"
            >
              <i className="fa fa-plus"></i> {t('button.add_correction')}
            </Link>
          )}
      </div>

      <h6 className="mb-3 fw-semibold text-secondary">{t('form.history_correction')}</h6>

      <div className="d-flex justify-content-center">
        <div
          className="table-responsive table-hover bg-white rounded shadow-sm w-100"
          style={{ maxWidth: '1000px' }}
        >
          <table className="table align-middle mb-0 w-100">
            <thead className="bg-light text-muted text-uppercase small">
              <tr>
                {headers.map((header, i) => {
                  const isFirst = i === 0
                  const isLast = i === headers.length - 1
                  return (
                    <th
                      key={header.key}
                      className={`py-2 px-3 fw-semibold text-black border-top border-bottom ${
                        isFirst ? 'border-start' : ''
                      } ${isLast ? 'border-end' : ''}`}
                    >
                      {header.label}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {items ? (
                items.map((row, index) => (
                  <tr
                    key={index}
                    className="border-bottom text-dark"
                    style={{ fontSize: '0.875rem', fontWeight: 400 }}
                  >
                    {headers.map(({ key }) => (
                      <td key={key} className="py-2 align-middle">
                        {key === 'initial_status' ? (
                          renderStatus(row[key])
                        ) : key === 'actions' ? (
                          <div className="d-flex justify-content-end gap-2">
                            <Link
                              to={`/order/correction/view/${row.uid}`}
                              className="d-inline btn rounded-pill btn-primary btn-xs my-2 mx-1 font-size-10"
                            >
                              {t('button.view')}
                            </Link>
                            {row.initial_status === 'REQUEST' && (
                              <button
                                onClick={(e) => cancelCorrection(e, row)}
                                className="d-inline btn rounded-pill btn-danger pointer btn-xs my-2 mx-1 font-size-10"
                              >
                                {t('button.cancel')}
                              </button>
                            )}
                          </div>
                        ) : (
                          row[key]
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr
                  className="border-bottom text-dark"
                  style={{ fontSize: '0.875rem', fontWeight: 400 }}
                >
                  <td colSpan={headers.length} className="text-center">
                    {t('title.no_data')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* <div className="mt-4 small text-muted" style={{ maxWidth: '1000px', marginInline: 'auto' }}>
        <strong>S&K:</strong>
        <ul className="mb-0">
          <li>
            Tidak bisa tambah koreksi pesanan baru jika masih ada koreksi dengan status{' '}
            <em>Waiting Approval</em>
          </li>
          <li>
            Bisa cancel selama status masih <em>Waiting Approval</em>
          </li>
        </ul>
      </div> */}
    </div>
  )
}

export default OrderCorrectionTable
